import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import {
  Building, Users, CalendarDays, FileText, Receipt, CreditCard,
  Brain, Shield, AlertTriangle, Search, ChevronRight, LogOut, Eye,
  Settings, Activity, BarChart3, Flag, Download, Lock, Menu, X, Loader2,
  RefreshCw, AlertCircle, CheckCircle, ChevronLeft, Filter, Clock,
  TrendingUp, TrendingDown, Info, Calendar, HelpCircle, Server
} from "lucide-react";
import AdminEmailActivity from "@/components/admin/AdminEmailActivity";
import { AdminClinicPlan } from "@/components/admin/AdminClinicPlan";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const SS = { 
  active: "bg-[#205B5A]/10 text-[#205B5A]", 
  trial: "bg-blue-50 text-blue-600", 
  cancelled: "bg-red-50 text-red-400", 
  suspended: "bg-amber-50 text-amber-600", 
  archived: "bg-[#1A1B1A]/5 text-[#757675]" 
};

// Status filter options
const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "paying", label: "Paying" },
  { value: "suspended", label: "Suspended" },
  { value: "cancelled", label: "Cancelled" },
  { value: "archived", label: "Archived" },
];

// Date range options
const DATE_RANGE_OPTIONS = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

// Feature flag definitions
const FEATURE_FLAGS = [
  { key: "ai_notes", label: "AI Documentation", desc: "AI-assisted treatment notes" },
  { key: "body_charts", label: "Body Charts", desc: "Visual body diagrams" },
  { key: "forms", label: "Patient Forms", desc: "Digital consent and intake forms" },
  { key: "online_booking", label: "Online Booking", desc: "Public booking page" },
  { key: "invoices", label: "Invoicing", desc: "Invoice and payment tracking" },
  { key: "email_notifications", label: "Email Notifications", desc: "Automated email alerts" },
];

// UK format date
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "-";
  }
};

// UK format date with time
const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) + 
           ", " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "-";
  }
};

// Format number with commas
const formatNumber = (num) => {
  if (num === null || num === undefined) return "0";
  return num.toLocaleString("en-GB");
};

// Format bytes to MB
const formatMB = (bytes) => {
  if (!bytes) return "0";
  return (bytes / (1024 * 1024)).toFixed(1);
};

// Skeleton components
const SkeletonCard = () => (
  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5 animate-pulse">
    <div className="h-2.5 w-16 bg-white/10 rounded mb-2" />
    <div className="h-6 w-12 bg-white/10 rounded" />
  </div>
);

const SkeletonRow = ({ cols = 4 }) => (
  <tr className="border-b border-white/[0.03]">
    {Array(cols).fill(0).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3 bg-white/10 rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
      </td>
    ))}
  </tr>
);

// Toast notification
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg ${
      type === 'success' ? 'bg-[#205B5A] text-white' : 'bg-red-500/90 text-white'
    }`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      <span className="text-sm">{message}</span>
      <button onClick={onClose} className="ml-2 text-white/70 hover:text-white">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// Tooltip component
const Tooltip = ({ text, children }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex items-center">
      <div 
        onMouseEnter={() => setShow(true)} 
        onMouseLeave={() => setShow(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {show && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1A1B1A] text-white text-[10px] rounded-lg shadow-xl z-50 w-48 text-center">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A1B1A]" />
        </div>
      )}
    </div>
  );
};

// Severity badge
const SeverityBadge = ({ severity }) => {
  const colors = {
    high: "bg-red-500/20 text-red-400",
    medium: "bg-amber-500/20 text-amber-400",
    low: "bg-blue-500/20 text-blue-400"
  };
  return (
    <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase ${colors[severity] || colors.low}`}>
      {severity}
    </span>
  );
};

// Navigation tabs - all available tabs
const ALL_TABS = [
  { k: "overview", l: "Overview", icon: BarChart3, permission: null },  // Available to all
  { k: "clinics", l: "Clinics", icon: Building, permission: null },
  { k: "ai", l: "AI Usage", icon: Brain, permission: null },
  { k: "usage", l: "Product Usage", icon: Activity, permission: null },
  { k: "audit", l: "Audit Log", icon: Shield, permission: null },
  { k: "itops", l: "IT Operations", icon: Server, permission: "it_ops.summary.view" },
  { k: "team", l: "Platform Team", icon: Users, ownerOnly: true },
];

// Get tabs based on user role
const getVisibleTabs = (userInfo) => {
  if (!userInfo) return [];
  
  const isPlatformOwner = userInfo.is_platform_owner === true || userInfo.platform_role === "platform_owner";
  const permissions = userInfo.permissions || [];
  
  return ALL_TABS.filter(tab => {
    // Owner-only tabs (Platform Team)
    if (tab.ownerOnly) {
      return isPlatformOwner;
    }
    // Permission-based tabs
    if (tab.permission) {
      return isPlatformOwner || permissions.includes(tab.permission);
    }
    // All other tabs visible to everyone
    return true;
  });
};

// Sidebar content
const SidebarContent = ({ tab, onTabChange, onSignOut, environment, userInfo, visibleTabs }) => {
  const isPlatformOwner = userInfo?.is_platform_owner === true || userInfo?.platform_role === "platform_owner";
  const roleLabel = isPlatformOwner ? "Platform Owner" : "IT Admin";
  
  return (
    <>
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-sm font-medium text-white" style={H}>LumeroFlow</p>
        <p className="text-[9px] text-[#7EBDBC] uppercase tracking-wider">{roleLabel}</p>
        {userInfo?.full_name && (
          <p className="text-[10px] text-white/40 mt-0.5 truncate">{userInfo.full_name}</p>
        )}
        {environment && environment !== "production" && (
          <span className="mt-1 inline-block text-[8px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded uppercase">
            {environment}
          </span>
        )}
      </div>
      <nav className="flex-1 p-2 space-y-0.5" role="navigation" aria-label="Admin navigation">
        {visibleTabs.map(t => (
          <button 
            key={t.k} 
            onClick={() => onTabChange(t.k)} 
            data-testid={`admin-tab-${t.k}`}
            aria-current={tab === t.k ? "page" : undefined}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#7EBDBC]/50 ${
              tab === t.k ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70 hover:bg-white/5"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" />{t.l}
          </button>
        ))}
      </nav>
      <div className="p-3 border-t border-white/[0.06]">
        <button 
          onClick={onSignOut} 
          className="text-[10px] text-white/30 hover:text-white/60 flex items-center gap-1 focus:outline-none focus:text-white/60"
        >
          <LogOut className="w-3 h-3" /> Sign out
        </button>
      </div>
    </>
  );
};

// Date range selector component
const DateRangeSelector = ({ value, onChange, className = "" }) => (
  <div className={`relative ${className}`}>
    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs appearance-none cursor-pointer focus:outline-none focus:border-[#7EBDBC] min-w-[130px]"
    >
      {DATE_RANGE_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value} className="bg-[#131E1E] text-white">
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetNotice, setResetNotice] = useState("");

  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [attention, setAttention] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [clinicSearch, setClinicSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState("30d");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalClinics, setTotalClinics] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinicDetailLoading, setClinicDetailLoading] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [aiFailures, setAiFailures] = useState(null);
  const [productUsage, setProductUsage] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditActions, setAuditActions] = useState([]);
  const [auditActionFilter, setAuditActionFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState({});
  const [actionReason, setActionReason] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  
  // IT Operations state
  const [itOpsData, setItOpsData] = useState(null);
  const [itOpsIncidents, setItOpsIncidents] = useState([]);
  const [itOpsTotal, setItOpsTotal] = useState(0);
  const [itOpsPage, setItOpsPage] = useState(1);
  const [itOpsSeverityFilter, setItOpsSeverityFilter] = useState("");
  const [itOpsClinicFilter, setItOpsClinicFilter] = useState("");
  const [itOpsClinics, setItOpsClinics] = useState([]);
  const [itOpsLoading, setItOpsLoading] = useState(false);
  const [itOpsError, setItOpsError] = useState(null);
  
  // Platform Team state
  const [platformUsers, setPlatformUsers] = useState([]);
  const [platformOwner, setPlatformOwner] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "", platform_role: "it_admin" });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [selectedPlatformUser, setSelectedPlatformUser] = useState(null);
  
  // User info and role-based access
  const [userInfo, setUserInfo] = useState(null);
  const visibleTabs = getVisibleTabs(userInfo);
  const isPlatformOwner = userInfo?.is_platform_owner === true || userInfo?.platform_role === "platform_owner";
  
  const searchRequestRef = useRef(0);
  const menuButtonRef = useRef(null);

  // Debounce search
  useEffect(() => {
    setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(clinicSearch);
      setCurrentPage(1);
      setIsSearching(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [clinicSearch]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [statusFilter]);

  const adminApi = useCallback((method, url, data) => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    if (method === "get") return api.get(url, config);
    if (method === "put") return api.put(url, data, config);
    if (method === "delete") return api.delete(url, config);
    return api.post(url, data, config);
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const { data } = await api.post('/admin/login', { email, password });
      setToken(data.token);
      setAuthed(true);
      // Always take role and permissions from the server, never from a cached value
      try {
        const me = await api.get('/admin/me', { headers: { Authorization: `Bearer ${data.token}` } });
        setUserInfo(me.data);
      } catch (meErr) {
        setUserInfo(data.user);
      }
      // Load settings after login
      try {
        const settingsRes = await api.get('/admin/settings', { headers: { Authorization: `Bearer ${data.token}` } });
        setSettings(settingsRes.data);
      } catch (settingsErr) {
        console.warn("Failed to load settings:", settingsErr);
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const loadTab = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    setError(null);
    try {
      if (tab === "overview") {
        const [overviewRes, attentionRes] = await Promise.all([
          adminApi("get", `/admin/overview?date_range=${dateRange}`),
          adminApi("get", "/admin/attention")
        ]);
        setOverview(overviewRes.data);
        setAttention(attentionRes.data);
      }
      if (tab === "clinics") {
        const requestId = ++searchRequestRef.current;
        // Clinics are a fixed inventory - don't filter by date range
        const params = new URLSearchParams({
          search: debouncedSearch,
          status: statusFilter,
          page: currentPage.toString(),
          page_size: pageSize.toString()
        });
        const { data } = await adminApi("get", `/admin/clinics?${params}`);
        if (requestId === searchRequestRef.current) {
          setClinics(data.clinics);
          setTotalClinics(data.total);
          setTotalPages(data.total_pages);
        }
      }
      if (tab === "ai") {
        const [usageRes, failuresRes] = await Promise.all([
          adminApi("get", `/admin/ai-usage?date_range=${dateRange}`),
          adminApi("get", `/admin/ai-failures?date_range=${dateRange}&page_size=10`)
        ]);
        setAiUsage(usageRes.data);
        setAiFailures(failuresRes.data);
      }
      if (tab === "usage") {
        const { data } = await adminApi("get", `/admin/product-usage?date_range=${dateRange}`);
        setProductUsage(data);
      }
      if (tab === "audit") {
        const params = new URLSearchParams({
          date_range: dateRange,
          page: auditPage.toString(),
          limit: "50"
        });
        if (auditActionFilter) params.append("action", auditActionFilter);
        const { data } = await adminApi("get", `/admin/audit-logs?${params}`);
        setAuditLogs(data.logs);
        setAuditTotal(data.total);
        setAuditActions(data.available_actions || []);
      }
      if (tab === "team") {
        const { data } = await adminApi("get", "/platform/users");
        setPlatformUsers(data.users || []);
        setPlatformOwner(data.owner);
      }
      if (tab === "itops") {
        setItOpsLoading(true);
        setItOpsError(null);
        try {
          const params = new URLSearchParams({ date_range: dateRange, page: itOpsPage.toString(), page_size: "20" });
          if (itOpsSeverityFilter) params.append("severity", itOpsSeverityFilter);
          if (itOpsClinicFilter) params.append("clinic_id", itOpsClinicFilter);
          
          const [summaryRes, incidentsRes, clinicsRes] = await Promise.all([
            adminApi("get", `/admin/it-operations/summary?date_range=${dateRange}`),
            adminApi("get", `/admin/it-operations/incidents?${params}`),
            adminApi("get", "/admin/it-operations/clinics")
          ]);
          setItOpsData(summaryRes.data);
          setItOpsIncidents(incidentsRes.data.incidents || []);
          setItOpsTotal(incidentsRes.data.total || 0);
          setItOpsClinics(clinicsRes.data.clinics || []);
        } catch (err) {
          console.error("IT Operations load error:", err);
          setItOpsError(err.response?.data?.detail || "Failed to load IT Operations data");
        } finally {
          setItOpsLoading(false);
        }
      }
    } catch (err) {
      console.error("Admin tab load error:", err);
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        // Re-check the identity: either the session died or this role genuinely lacks access
        try {
          const me = await adminApi("get", "/admin/me");
          setUserInfo(me.data);
          setError(me.data.is_platform_owner
            ? "That section failed to load. Please retry - your Platform Owner access is intact."
            : `Your role (${me.data.platform_role === "it_admin" ? "IT Admin" : me.data.platform_role}) does not have access to this section.`);
        } catch {
          setAuthed(false);
          setToken("");
          setUserInfo(null);
          setLoginError("Your admin session is no longer valid. Please sign in again.");
        }
      } else {
        setError(err.response?.data?.detail || "Failed to load data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [authed, tab, debouncedSearch, statusFilter, currentPage, pageSize, adminApi, dateRange, auditPage, auditActionFilter, itOpsPage, itOpsSeverityFilter, itOpsClinicFilter]);

  useEffect(() => { loadTab(); }, [loadTab]);

  const openClinic = async (id) => {
    setClinicDetailLoading(true);
    try {
      const { data } = await adminApi("get", `/admin/clinics/${id}`);
      setSelectedClinic(data);
    } catch (err) {
      setToast({ message: "Failed to load clinic details", type: "error" });
    } finally {
      setClinicDetailLoading(false);
    }
  };

  const updateFeatureFlag = async (flagKey, newValue) => {
    if (!selectedClinic || featureFlagsLoading) return;
    setFeatureFlagsLoading(true);
    try {
      const updateData = { [flagKey]: newValue };
      await adminApi("put", `/admin/feature-flags/${selectedClinic.id}`, updateData);
      const { data } = await adminApi("get", `/admin/clinics/${selectedClinic.id}`);
      setSelectedClinic(data);
      setToast({ message: `Feature flag updated`, type: "success" });
    } catch (err) {
      setToast({ message: "Failed to update feature flag", type: "error" });
    } finally {
      setFeatureFlagsLoading(false);
    }
  };

  const clinicAction = async (clinicId, action, reason = "") => {
    if (actionLoading) return;
    
    // Require confirmation for sensitive actions
    const sensitiveActions = ["suspend", "cancel", "archive"];
    if (sensitiveActions.includes(action) && !confirmAction) {
      setConfirmAction({ clinicId, action });
      return;
    }
    
    // Require reason for sensitive actions
    if (sensitiveActions.includes(action) && !reason.trim()) {
      setToast({ message: "Please provide a reason for this action", type: "error" });
      return;
    }
    
    setActionLoading(action);
    try {
      await adminApi("post", `/admin/clinics/${clinicId}/action`, { action, reason });
      setToast({ message: `Clinic ${action.replace(/_/g, " ")} successful`, type: "success" });
      setConfirmAction(null);
      setActionReason("");
      loadTab();
      if (selectedClinic?.id === clinicId) {
        openClinic(clinicId);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.detail || `Failed to ${action.replace(/_/g, " ")} clinic`, type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const exportCSV = async (type) => {
    try {
      let endpoint = `/admin/export/${type}`;
      const params = new URLSearchParams({ date_range: dateRange });
      if (type === "clinics") {
        if (debouncedSearch) params.append("search", debouncedSearch);
        if (statusFilter !== "all") params.append("status", statusFilter);
      }
      
      const response = await adminApi("get", `${endpoint}?${params}`);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lumeroflow-${type}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setToast({ message: `Exported ${type} successfully`, type: "success" });
    } catch (err) {
      setToast({ message: `Failed to export ${type}`, type: "error" });
    }
  };

  // Platform Team functions
  const createInvite = async () => {
    if (!inviteForm.email || !inviteForm.full_name) {
      setToast({ message: "Please fill in all required fields", type: "error" });
      return;
    }
    setInviteLoading(true);
    try {
      const { data } = await adminApi("post", "/platform/invite", inviteForm);
      setInviteLink(data.invite_link);
      setToast({ message: "Invitation created successfully", type: "success" });
      loadTab();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to create invitation", type: "error" });
    } finally {
      setInviteLoading(false);
    }
  };

  const copyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setToast({ message: "Invite link copied to clipboard", type: "success" });
    }
  };

  const revokeInvite = async (inviteId) => {
    setActionLoading("revoke_" + inviteId);
    try {
      await adminApi("post", `/platform/invite/${inviteId}/revoke`);
      setToast({ message: "Invitation revoked", type: "success" });
      loadTab();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to revoke invitation", type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const regenerateInvite = async (inviteId) => {
    setActionLoading("regenerate_" + inviteId);
    try {
      const { data } = await adminApi("post", `/platform/invite/${inviteId}/regenerate`);
      setInviteLink(data.invite_link);
      setToast({ message: "New invite link generated", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to regenerate invite", type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const deactivatePlatformUser = async (userId, reason) => {
    setActionLoading("deactivate_" + userId);
    try {
      await adminApi("post", `/platform/users/${userId}/deactivate?reason=${encodeURIComponent(reason || "")}`);
      setToast({ message: "User deactivated", type: "success" });
      setSelectedPlatformUser(null);
      loadTab();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to deactivate user", type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const reactivatePlatformUser = async (userId) => {
    setActionLoading("reactivate_" + userId);
    try {
      await adminApi("post", `/platform/users/${userId}/reactivate`);
      setToast({ message: "User reactivated", type: "success" });
      loadTab();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to reactivate user", type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const revokeSessions = async (userId, sessionId = null) => {
    setActionLoading("revoke_sessions_" + userId);
    try {
      const params = sessionId ? `?session_id=${sessionId}` : "";
      const { data } = await adminApi("post", `/platform/users/${userId}/revoke-sessions${params}`);
      setToast({ message: data.message, type: "success" });
      loadTab();
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to revoke sessions", type: "error" });
    } finally {
      setActionLoading(null);
    }
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setMobileMenuOpen(false);
  };

  // Close mobile menu on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && mobileMenuOpen) {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  // ── Login screen ──
  if (!authed) return (
    <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Lock className="w-8 h-8 text-[#7EBDBC] mx-auto mb-3" />
          <h1 className="text-xl font-medium text-white" style={H}>LumeroFlow Admin</h1>
          <p className="text-xs text-white/50 mt-1">Internal super-admin access only</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          {loginError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {loginError}
            </div>
          )}
          <input 
            type="email" 
            value={email} 
            onChange={e => setEmail(e.target.value)} 
            required 
            placeholder="Admin email" 
            data-testid="admin-email"
            disabled={loginLoading}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] disabled:opacity-50" 
          />
          <input 
            type="password" 
            value={password} 
            onChange={e => setPassword(e.target.value)} 
            required 
            placeholder="Password" 
            data-testid="admin-password"
            disabled={loginLoading}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] disabled:opacity-50" 
          />
          <button 
            type="submit" 
            data-testid="admin-login-btn" 
            disabled={loginLoading}
            className="w-full bg-[#205B5A] text-white py-3 rounded-full font-medium text-sm hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loginLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loginLoading ? "Signing in..." : "Sign in to admin"}
          </button>
          <div className="text-center">
            <button type="button" data-testid="admin-forgot-password-btn"
              disabled={resetSending}
              onClick={async () => {
                if (!email) { setResetNotice("Enter your admin email first, then choose Forgot password."); return; }
                setResetSending(true);
                setResetNotice("");
                try {
                  const { data } = await api.post("/platform/owner-reset/request", { email });
                  setResetNotice(data.message);
                } catch (err) {
                  setResetNotice(err.response?.data?.detail
                    || "Could not send a reset link right now. Please try again shortly.");
                }
                setResetSending(false);
              }}
              className="text-xs text-white/40 hover:text-[#7EBDBC] transition-colors disabled:opacity-50">
              {resetSending ? "Sending reset link..." : "Forgot password?"}
            </button>
          </div>
          {resetNotice && (
            <p className="text-xs text-[#7EBDBC] text-center" data-testid="admin-reset-notice">
              {resetNotice}
            </p>
          )}
        </form>
      </div>
    </div>
  );

  const handleSignOut = () => {
    setAuthed(false);
    setToken("");
    setMobileMenuOpen(false);
    setSettings(null);
  };

  return (
    <div className="min-h-screen bg-[#0F1A1A] flex" data-testid="admin-panel">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-[200px] bg-[#131E1E] border-r border-white/[0.06] flex-col flex-shrink-0">
        <SidebarContent 
          tab={tab} 
          onTabChange={handleTabChange} 
          onSignOut={handleSignOut} 
          environment={settings?.environment}
          userInfo={userInfo}
          visibleTabs={visibleTabs}
        />
      </aside>

      {/* Mobile Menu Button */}
      <button
        ref={menuButtonRef}
        onClick={() => setMobileMenuOpen(true)}
        className="md:hidden fixed top-4 left-4 z-40 p-2 bg-[#131E1E] rounded-lg border border-white/10 text-white/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#7EBDBC]/50"
        aria-label="Open navigation menu"
        aria-expanded={mobileMenuOpen}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} aria-hidden="true" />
          <aside className="absolute left-0 top-0 bottom-0 w-[240px] bg-[#131E1E] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-white/[0.06]">
              <div>
                <p className="text-sm font-medium text-white" style={H}>LumeroFlow</p>
                <p className="text-[9px] text-[#7EBDBC] uppercase tracking-wider">
                  {isPlatformOwner ? "Platform Owner" : "IT Admin"}
                </p>
                {userInfo?.full_name && (
                  <p className="text-[10px] text-white/40 mt-0.5 truncate">{userInfo.full_name}</p>
                )}
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-white/40 hover:text-white p-1" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-2 space-y-0.5" role="navigation">
              {visibleTabs.map(t => (
                <button 
                  key={t.k} 
                  onClick={() => handleTabChange(t.k)}
                  data-testid={`admin-tab-mobile-${t.k}`}
                  className={`w-full flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-colors ${
                    tab === t.k ? "bg-white/10 text-white" : "text-white/40 hover:text-white/70 hover:bg-white/5"
                  }`}
                >
                  <t.icon className="w-4 h-4" />{t.l}
                </button>
              ))}
            </nav>
            <div className="p-3 border-t border-white/[0.06]">
              <button onClick={handleSignOut} className="text-xs text-white/30 hover:text-white/60 flex items-center gap-1">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-16 md:pt-6">
        {/* Error State */}
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div className="flex-1"><p className="text-sm text-red-300">{error}</p></div>
            <button onClick={loadTab} className="text-xs text-red-300 hover:text-red-200 flex items-center gap-1">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        )}

        {/* OVERVIEW */}
        {tab === "overview" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h1 className="text-xl font-medium text-white" style={H}>Overview</h1>
              <DateRangeSelector value={dateRange} onChange={setDateRange} />
            </div>
            
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {Array(12).fill(0).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : overview ? (
              <>
                {/* Main metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { l: "Total clinics", v: overview.total_clinics, c: "#7EBDBC", tip: "All registered clinics (all-time)" },
                    { l: "Paying", v: overview.paying_clinics, c: "#205B5A" },
                    { l: "Trial", v: overview.trial_clinics, c: "#496275" },
                    { l: "Cancelled", v: overview.cancelled_clinics, c: "#C4553A" },
                    { l: "Users", v: overview.total_users, c: "#7EBDBC", tip: "All platform users (all-time)" },
                    { l: "Patients", v: overview.total_patients, c: "#496275", tip: "All registered patients (all-time)" },
                    { l: "Appointments", v: overview.appointments, c: "#205B5A", tip: `Created in ${dateRange === 'all' ? 'all time' : 'selected period'}` },
                    { l: "Notes", v: overview.notes_created, c: "#496275", tip: `Created in ${dateRange === 'all' ? 'all time' : 'selected period'}` },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5">
                      <div className="flex items-center gap-1">
                        <p className="text-[10px] text-white/40 uppercase tracking-wider">{s.l}</p>
                        {s.tip && (
                          <Tooltip text={s.tip}>
                            <HelpCircle className="w-3 h-3 text-white/20" />
                          </Tooltip>
                        )}
                      </div>
                      <p className="text-xl font-medium mt-0.5" style={{ ...H, color: s.c }}>{formatNumber(s.v)}</p>
                    </div>
                  ))}
                </div>

                {/* AI Reliability Section */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-4 h-4 text-[#7EBDBC]" />
                    <h2 className="text-sm font-medium text-white">AI Reliability</h2>
                    <Tooltip text="AI Job: One clinic workflow (recording → transcription → note). A job may generate multiple API requests.">
                      <HelpCircle className="w-3 h-3 text-white/20" />
                    </Tooltip>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[10px] text-white/40 uppercase">Success Rate</p>
                      <p className={`text-lg font-medium ${overview.ai_success_rate >= 80 ? 'text-[#7EBDBC]' : overview.ai_success_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {overview.ai_success_rate}%
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase">Failed (7d)</p>
                      <p className={`text-lg font-medium ${overview.ai_failed_7d === 0 ? 'text-[#7EBDBC]' : 'text-red-400'}`}>
                        {overview.ai_failed_7d}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase">Failed (30d)</p>
                      <p className={`text-lg font-medium ${overview.ai_failed_30d === 0 ? 'text-[#7EBDBC]' : overview.ai_failed_30d < 5 ? 'text-amber-400' : 'text-red-400'}`}>
                        {overview.ai_failed_30d}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-white/40 uppercase">Top Failure</p>
                      <p className="text-sm text-white/70 capitalize">
                        {overview.ai_top_failure_category?.replace(/_/g, " ") || "None"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/[0.06] grid grid-cols-2 md:grid-cols-4 gap-4 text-[10px]">
                    <div>
                      <span className="text-white/40">Last success:</span>
                      <span className="text-white/60 ml-1">{overview.ai_latest_success ? formatDateTime(overview.ai_latest_success) : "Never"}</span>
                    </div>
                    <div>
                      <span className="text-white/40">Last failure:</span>
                      <span className="text-white/60 ml-1">{overview.ai_latest_failure ? formatDateTime(overview.ai_latest_failure) : "Never"}</span>
                    </div>
                    <div>
                      <span className="text-white/40">Retried:</span>
                      <span className="text-white/60 ml-1">{overview.ai_retry_total} jobs</span>
                    </div>
                    <div>
                      <span className="text-white/40">Retry success:</span>
                      <span className="text-white/60 ml-1">{overview.ai_retry_success} jobs</span>
                    </div>
                  </div>
                </div>

                {/* Attention Required */}
                {attention && attention.items?.length > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <h2 className="text-sm font-medium text-amber-400">Attention Required</h2>
                      <span className="text-[10px] text-amber-400/60">({attention.total} clinics)</span>
                    </div>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {attention.items.slice(0, 5).map((item, i) => (
                        <div 
                          key={item.clinic_id}
                          className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg cursor-pointer hover:bg-white/[0.04]"
                          onClick={() => openClinic(item.clinic_id)}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-white/80 font-medium truncate">{item.clinic_name}</p>
                            <p className="text-[10px] text-white/40">{item.reasons.join(" • ")}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <SeverityBadge severity={item.severity} />
                            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
                          </div>
                        </div>
                      ))}
                    </div>
                    {attention.items.length > 5 && (
                      <button 
                        onClick={() => setTab("clinics")}
                        className="mt-2 text-[10px] text-amber-400 hover:underline"
                      >
                        View all {attention.items.length} clinics →
                      </button>
                    )}
                  </div>
                )}

                {/* Clinic Status Summary */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="w-4 h-4 text-[#7EBDBC]" />
                    <h2 className="text-sm font-medium text-white">Clinic Status</h2>
                  </div>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {[
                      { l: "Active", v: overview.paying_clinics, c: "text-[#205B5A]" },
                      { l: "Trial", v: overview.trial_clinics, c: "text-blue-400" },
                      { l: "Suspended", v: overview.suspended_clinics, c: "text-amber-400" },
                      { l: "Cancelled", v: overview.cancelled_clinics, c: "text-red-400" },
                      { l: "Ending 7d", v: overview.trials_ending_7d, c: overview.trials_ending_7d > 0 ? "text-amber-400" : "text-white/40" },
                      { l: "Ending 30d", v: overview.trials_ending_30d, c: "text-white/40" },
                    ].map((s, i) => (
                      <div key={i} className="text-center">
                        <p className={`text-lg font-medium ${s.c}`}>{s.v}</p>
                        <p className="text-[9px] text-white/40 uppercase">{s.l}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-[#7EBDBC]" />
                    <h2 className="text-sm font-medium text-white">Recent Activity</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                    <div className="flex justify-between">
                      <span className="text-white/40">Latest clinic:</span>
                      <span className="text-white/70">{overview.latest_clinic_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/40">Created:</span>
                      <span className="text-white/70">{formatDate(overview.latest_clinic_created)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/40">Latest admin action:</span>
                      <span className="text-white/70 capitalize">{overview.latest_admin_action?.replace(/_/g, " ") || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white/40">At:</span>
                      <span className="text-white/70">{formatDateTime(overview.latest_admin_action_at)}</span>
                    </div>
                  </div>
                </div>
              </>
            ) : !error && (
              <div className="text-center py-12">
                <BarChart3 className="w-8 h-8 text-white/20 mx-auto mb-3" />
                <p className="text-sm text-white/40">No overview data available</p>
              </div>
            )}
          </div>
        )}

        {/* CLINICS */}
        {tab === "clinics" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-medium text-white" style={H}>Clinics</h1>
                <span className="text-xs text-white/40">{totalClinics} total</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    data-testid="admin-status-filter"
                    className="pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs appearance-none cursor-pointer focus:outline-none focus:border-[#7EBDBC] min-w-[140px]"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-[#131E1E] text-white">{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                  {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#7EBDBC] animate-spin" />}
                  <input 
                    value={clinicSearch} 
                    onChange={e => setClinicSearch(e.target.value)} 
                    placeholder="Search clinics..." 
                    data-testid="admin-clinic-search"
                    className="pl-9 pr-9 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] w-full sm:w-56" 
                  />
                </div>
                <button
                  onClick={() => exportCSV("clinics")}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs hover:bg-white/10"
                  title="Export to CSV"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Clinic</th>
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Owner</th>
                      <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Users</th>
                      <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Practitioners</th>
                      <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Patients</th>
                      <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">AI Jobs</th>
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Status</th>
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={8} />)
                    ) : clinics.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center">
                          <Building className="w-8 h-8 text-white/20 mx-auto mb-3" />
                          <p className="text-sm text-white/40">
                            {clinicSearch && statusFilter !== "all" 
                              ? `No ${statusFilter} clinics match "${clinicSearch}"`
                              : clinicSearch 
                                ? `No clinics match "${clinicSearch}"`
                                : statusFilter !== "all"
                                  ? `No ${statusFilter} clinics exist`
                                  : "No clinics exist yet"}
                          </p>
                          {(clinicSearch || statusFilter !== "all") && (
                            <button onClick={() => { setClinicSearch(""); setStatusFilter("all"); }} className="mt-2 text-xs text-[#7EBDBC] hover:underline">
                              Clear filters
                            </button>
                          )}
                        </td>
                      </tr>
                    ) : (
                      [...new Map(clinics.map(c => [c.id, c])).values()].map((c, idx) => (
                        <tr 
                          key={`${c.id}-${idx}`} 
                          className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer" 
                          onClick={() => openClinic(c.id)}
                        >
                          <td className="px-4 py-2.5 text-xs text-white/80 font-medium">{c.name}</td>
                          <td className="px-4 py-2.5 text-xs text-white/50">{c.owner_name}</td>
                          <td className="px-4 py-2.5 text-xs text-white/50 text-right">{c.user_count || 0}</td>
                          <td className="px-4 py-2.5 text-xs text-white/50 text-right">{c.practitioner_count || 0}</td>
                          <td className="px-4 py-2.5 text-xs text-white/50 text-right">{c.patient_count || 0}</td>
                          <td className="px-4 py-2.5 text-xs text-white/50 text-right">{c.ai_job_count || 0}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${SS[c.subscription_status] || SS.trial}`}>
                              {c.subscription_status || "trial"}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-white/40">{formatDate(c.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {(totalPages > 1 || totalClinics > 10) && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-white/40">Page {currentPage} of {totalPages}</span>
                    <select
                      value={pageSize}
                      onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                      className="px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-xs appearance-none cursor-pointer focus:outline-none"
                    >
                      <option value={25} className="bg-[#131E1E]">25 / page</option>
                      <option value={50} className="bg-[#131E1E]">50 / page</option>
                      <option value={100} className="bg-[#131E1E]">100 / page</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1 || loading}
                      className="p-1.5 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages || loading}
                      className="p-1.5 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Next page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI USAGE */}
        {tab === "ai" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h1 className="text-xl font-medium text-white" style={H}>AI Usage</h1>
              <div className="flex gap-2">
                <DateRangeSelector value={dateRange} onChange={setDateRange} />
                <button
                  onClick={() => exportCSV("ai-usage")}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs hover:bg-white/10"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
            
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {Array(4).fill(0).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : aiUsage ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5">
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] text-white/40 uppercase">Total Jobs</p>
                      <Tooltip text={aiUsage.metric_definitions?.ai_job || "A clinic workflow"}>
                        <HelpCircle className="w-3 h-3 text-white/20" />
                      </Tooltip>
                    </div>
                    <p className="text-xl font-medium text-[#7EBDBC]" style={H}>{formatNumber(aiUsage.total_jobs)}</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5">
                    <p className="text-[10px] text-white/40 uppercase">Success Rate</p>
                    <p className={`text-xl font-medium ${aiUsage.success_rate >= 80 ? 'text-[#7EBDBC]' : aiUsage.success_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`} style={H}>
                      {aiUsage.success_rate}%
                    </p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5">
                    <div className="flex items-center gap-1">
                      <p className="text-[10px] text-white/40 uppercase">API Requests</p>
                      <Tooltip text={aiUsage.metric_definitions?.ai_request || "Individual API calls"}>
                        <HelpCircle className="w-3 h-3 text-white/20" />
                      </Tooltip>
                    </div>
                    <p className="text-xl font-medium text-[#7EBDBC]" style={H}>{formatNumber(aiUsage.total_logs)}</p>
                  </div>
                  <div className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5">
                    <p className="text-[10px] text-white/40 uppercase">Failed Jobs</p>
                    <p className={`text-xl font-medium ${aiUsage.failed_jobs === 0 ? 'text-[#7EBDBC]' : 'text-red-400'}`} style={H}>
                      {formatNumber(aiUsage.failed_jobs)}
                    </p>
                  </div>
                </div>

                {/* Failure Breakdown */}
                {Object.keys(aiUsage.failure_breakdown || {}).length > 0 && (
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-6">
                    <h3 className="text-sm font-medium text-white mb-3">Failure Breakdown</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(aiUsage.failure_breakdown).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                        <div key={cat} className="flex justify-between items-center p-2 bg-white/[0.02] rounded">
                          <span className="text-xs text-white/60 capitalize">{cat.replace(/_/g, " ")}</span>
                          <span className="text-xs text-red-400 font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Failures */}
                {aiFailures && aiFailures.failures?.length > 0 && (
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden mb-6">
                    <div className="px-4 py-3 border-b border-white/[0.06]">
                      <h3 className="text-sm font-medium text-white">Recent Failures</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[600px]">
                        <thead>
                          <tr className="border-b border-white/[0.06]">
                            <th className="text-left px-4 py-2 text-[9px] text-white/30 uppercase">Time</th>
                            <th className="text-left px-4 py-2 text-[9px] text-white/30 uppercase">Clinic</th>
                            <th className="text-left px-4 py-2 text-[9px] text-white/30 uppercase">Reference</th>
                            <th className="text-left px-4 py-2 text-[9px] text-white/30 uppercase">Failed stage</th>
                            <th className="text-left px-4 py-2 text-[9px] text-white/30 uppercase">Code</th>
                            <th className="text-left px-4 py-2 text-[9px] text-white/30 uppercase">Error</th>
                            <th className="text-center px-4 py-2 text-[9px] text-white/30 uppercase">Retries</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiFailures.failures.slice(0, 10).map((f, i) => (
                            <tr key={f.job_id || i} className="border-b border-white/[0.03]">
                              <td className="px-4 py-2 text-[10px] text-white/40">{formatDateTime(f.timestamp)}</td>
                              <td className="px-4 py-2 text-xs text-white/60">{f.clinic_name}</td>
                              <td className="px-4 py-2 text-[10px] font-mono text-white/60" data-testid={`ai-failure-reference-${i}`}>{f.reference_id || "—"}</td>
                              <td className="px-4 py-2 text-[10px] text-white/50" data-testid={`ai-failure-stage-${i}`}>
                                {f.failed_stage_label || f.failed_stage?.replace(/_/g, " ") || "—"}
                                {f.total_chunks ? ` (${f.chunks_completed ?? 0}/${f.total_chunks} parts)` : ""}
                              </td>
                              <td className="px-4 py-2">
                                <span className="text-[9px] px-2 py-0.5 bg-red-500/10 text-red-400 rounded-full font-mono" data-testid={`ai-failure-code-${i}`}>
                                  {f.error_code || f.error_category?.replace(/_/g, " ") || "unknown"}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-[10px] text-white/50 max-w-[200px] truncate" title={f.error_message}>{f.error_message}</td>
                              <td className="px-4 py-2 text-xs text-white/50 text-center">{f.retry_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Usage by Clinic */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/[0.06]">
                    <h3 className="text-sm font-medium text-white">Usage by Clinic</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead>
                        <tr className="border-b border-white/[0.06]">
                          <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Clinic</th>
                          <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Requests</th>
                          <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Successful</th>
                          <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">
                            <div className="flex items-center justify-end gap-1">
                              Tokens
                              <Tooltip text={aiUsage.metric_definitions?.tokens || "LLM tokens consumed"}>
                                <HelpCircle className="w-3 h-3 text-white/20" />
                              </Tooltip>
                            </div>
                          </th>
                          <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Audio MB</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiUsage.by_clinic?.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-12 text-center">
                              <Brain className="w-8 h-8 text-white/20 mx-auto mb-3" />
                              <p className="text-sm text-white/40">No AI usage data recorded yet</p>
                            </td>
                          </tr>
                        ) : (
                          aiUsage.by_clinic?.map(c => (
                            <tr key={c.clinic_id} className="border-b border-white/[0.03]">
                              <td className="px-4 py-2.5 text-xs text-white/80">{c.clinic_name || c.clinic_id}</td>
                              <td className="px-4 py-2.5 text-xs text-white/50 text-right">{formatNumber(c.requests)}</td>
                              <td className="px-4 py-2.5 text-xs text-[#7EBDBC] text-right">{formatNumber(c.successful)}</td>
                              <td className="px-4 py-2.5 text-xs text-white/50 text-right">{formatNumber(c.tokens)}</td>
                              <td className="px-4 py-2.5 text-xs text-white/50 text-right">{formatMB(c.audio_bytes)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : !error && (
              <div className="text-center py-12">
                <Brain className="w-8 h-8 text-white/20 mx-auto mb-3" />
                <p className="text-sm text-white/40">No AI usage data available</p>
              </div>
            )}
          </div>
        )}

        {/* PRODUCT USAGE */}
        {tab === "usage" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h1 className="text-xl font-medium text-white" style={H}>Product Usage</h1>
              <DateRangeSelector value={dateRange} onChange={setDateRange} />
            </div>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array(8).fill(0).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : productUsage && Object.keys(productUsage).length > 0 ? (
              <>
                {/* Show metric definitions note */}
                <p className="text-[10px] text-white/40 mb-4">
                  * Patients and Users are all-time totals. Other metrics respect the selected date range.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(productUsage)
                    .filter(([k]) => !["date_range", "metric_definitions"].includes(k))
                    .map(([k, v]) => {
                      const def = productUsage.metric_definitions?.[k];
                      return (
                        <div key={k} className="bg-white/[0.04] border border-white/[0.06] rounded-xl p-3.5">
                          <div className="flex items-center gap-1">
                            <p className="text-[10px] text-white/40 uppercase tracking-wider">{k.replace(/_/g, " ")}</p>
                            {def && (
                              <Tooltip text={def}>
                                <HelpCircle className="w-3 h-3 text-white/20" />
                              </Tooltip>
                            )}
                          </div>
                          <p className="text-xl font-medium text-[#7EBDBC] mt-0.5" style={H}>{formatNumber(v)}</p>
                        </div>
                      );
                    })}
                </div>
              </>
            ) : !error && (
              <div className="text-center py-12">
                <Activity className="w-8 h-8 text-white/20 mx-auto mb-3" />
                <p className="text-sm text-white/40">No product usage data available</p>
              </div>
            )}
          </div>
        )}

        {/* AUDIT LOG */}
        {tab === "audit" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h1 className="text-xl font-medium text-white" style={H}>Audit Log</h1>
              <div className="flex flex-wrap gap-2">
                <DateRangeSelector value={dateRange} onChange={setDateRange} />
                <select
                  value={auditActionFilter}
                  onChange={e => { setAuditActionFilter(e.target.value); setAuditPage(1); }}
                  className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs appearance-none cursor-pointer focus:outline-none focus:border-[#7EBDBC] min-w-[140px]"
                >
                  <option value="" className="bg-[#131E1E]">All actions</option>
                  {auditActions.map(a => (
                    <option key={a} value={a} className="bg-[#131E1E] capitalize">{a.replace(/_/g, " ")}</option>
                  ))}
                </select>
                <button
                  onClick={() => exportCSV("audit-logs")}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs hover:bg-white/10"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
              {loading ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Time</th>
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Admin</th>
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Action</th>
                      <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array(5).fill(0).map((_, i) => <SkeletonRow key={i} cols={4} />)}
                  </tbody>
                </table>
              ) : auditLogs.length === 0 ? (
                <div className="px-4 py-12 text-center">
                  <Shield className="w-8 h-8 text-white/20 mx-auto mb-3" />
                  <p className="text-sm text-white/40">No audit events recorded yet</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead>
                        <tr className="border-b border-white/[0.06]">
                          <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Time</th>
                          <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Admin</th>
                          <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Action</th>
                          <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Clinic</th>
                          <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Detail</th>
                          <th className="text-center px-4 py-2.5 text-[9px] text-white/30 uppercase">Tech</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.map((log, idx) => (
                          <tr key={log.id || `audit-${idx}`} className="border-b border-white/[0.03]">
                            <td className="px-4 py-2 text-[10px] text-white/40 whitespace-nowrap">
                              {formatDateTime(log.created_at)}
                            </td>
                            <td className="px-4 py-2 text-xs text-white/60">{log.admin_name || "System"}</td>
                            <td className="px-4 py-2 text-xs text-[#7EBDBC] capitalize whitespace-nowrap">
                              {log.action?.replace(/_/g, " ")}
                            </td>
                            <td className="px-4 py-2 text-xs text-white/50">{log.clinic_name || "-"}</td>
                            <td className="px-4 py-2 text-[10px] text-white/40 max-w-[200px] truncate">
                              {log.detail}
                            </td>
                            <td className="px-4 py-2 text-center">
                              {log.metadata && (
                                <button
                                  onClick={() => setShowTechnicalDetails(prev => ({ ...prev, [log.id]: !prev[log.id] }))}
                                  className="text-[10px] text-white/30 hover:text-white/60"
                                  title="View technical details"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination */}
                  {auditTotal > 50 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                      <span className="text-xs text-white/40">
                        Showing {(auditPage - 1) * 50 + 1}-{Math.min(auditPage * 50, auditTotal)} of {auditTotal}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                          disabled={auditPage === 1}
                          className="p-1.5 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setAuditPage(p => p + 1)}
                          disabled={auditPage * 50 >= auditTotal}
                          className="p-1.5 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* IT OPERATIONS */}
        {tab === "itops" && (
          <div data-testid="it-operations-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div>
                <h1 className="text-xl font-medium text-white" style={H}>IT Operations</h1>
                <p className="text-xs text-white/40 mt-1">Monitor system health, incidents, and service status</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <DateRangeSelector value={dateRange} onChange={setDateRange} />
                <button
                  onClick={loadTab}
                  disabled={itOpsLoading}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-xs hover:bg-white/10 disabled:opacity-50"
                  data-testid="it-ops-refresh-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${itOpsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>

            {/* Error State */}
            {itOpsError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-5 flex items-center justify-between" data-testid="it-ops-error">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-sm text-red-300">Failed to load data</p>
                    <p className="text-xs text-red-300/60">{itOpsError}</p>
                  </div>
                </div>
                <button onClick={loadTab} className="px-3 py-1.5 bg-red-500/20 text-red-300 text-xs rounded-lg hover:bg-red-500/30">
                  Retry
                </button>
              </div>
            )}

            {/* Loading State */}
            {itOpsLoading && !itOpsData && (
              <div className="space-y-4" data-testid="it-ops-loading">
                {/* Skeleton metrics */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[1,2,3,4,5,6].map(i => (
                    <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 animate-pulse">
                      <div className="h-3 bg-white/10 rounded w-20 mb-2" />
                      <div className="h-6 bg-white/10 rounded w-12" />
                    </div>
                  ))}
                </div>
                {/* Skeleton table */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-32 mb-4" />
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="h-10 bg-white/5 rounded mb-2" />
                  ))}
                </div>
              </div>
            )}

            {/* Main Content */}
            {!itOpsLoading || itOpsData ? (
              <>
                {/* Summary Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="it-ops-metrics">
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Total Incidents</p>
                    <p className="text-2xl font-medium text-white">{itOpsData?.metrics?.total_incidents ?? 0}</p>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Critical</p>
                    <p className="text-2xl font-medium text-red-400">{itOpsData?.metrics?.critical_incidents ?? 0}</p>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Last 24h</p>
                    <p className="text-2xl font-medium text-amber-400">{itOpsData?.metrics?.failures_24h ?? 0}</p>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Affected Clinics</p>
                    <p className="text-2xl font-medium text-white">{itOpsData?.metrics?.affected_clinics ?? 0}</p>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Retryable</p>
                    <p className="text-2xl font-medium text-blue-400">{itOpsData?.metrics?.retryable_failures ?? 0}</p>
                  </div>
                  <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Resolved</p>
                    <p className="text-2xl font-medium text-[#7EBDBC]">{itOpsData?.metrics?.resolved_incidents ?? 0}</p>
                  </div>
                </div>

                {/* Service Health */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 mb-6" data-testid="it-ops-service-health">
                  <h2 className="text-sm font-medium text-white mb-3" style={H}>Service Health</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {itOpsData?.service_health && Object.entries(itOpsData.service_health).map(([key, service]) => (
                      <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02]">
                        <div className={`w-2 h-2 rounded-full ${
                          service.status === 'healthy' ? 'bg-[#7EBDBC]' : 
                          service.status === 'degraded' ? 'bg-amber-400' : 
                          service.status === 'down' ? 'bg-red-400' : 'bg-white/20'
                        }`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] text-white/60 capitalize truncate">{key.replace(/_/g, ' ')}</p>
                          <p className="text-[9px] text-white/30 truncate">{service.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Incidents Filters */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <select
                    value={itOpsSeverityFilter}
                    onChange={e => { setItOpsSeverityFilter(e.target.value); setItOpsPage(1); }}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs appearance-none cursor-pointer focus:outline-none focus:border-[#7EBDBC]"
                    data-testid="it-ops-severity-filter"
                  >
                    <option value="" className="bg-[#131E1E]">All severities</option>
                    <option value="critical" className="bg-[#131E1E]">Critical</option>
                    <option value="warning" className="bg-[#131E1E]">Warning</option>
                  </select>
                  <select
                    value={itOpsClinicFilter}
                    onChange={e => { setItOpsClinicFilter(e.target.value); setItOpsPage(1); }}
                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs appearance-none cursor-pointer focus:outline-none focus:border-[#7EBDBC] max-w-[200px]"
                    data-testid="it-ops-clinic-filter"
                  >
                    <option value="" className="bg-[#131E1E]">All clinics</option>
                    {itOpsClinics.map(c => (
                      <option key={c.id} value={c.id} className="bg-[#131E1E]">{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Incidents Table */}
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="it-ops-incidents-table">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/[0.06]">
                        <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Time</th>
                        <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Severity</th>
                        <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase hidden md:table-cell">Clinic</th>
                        <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase hidden lg:table-cell">Operation</th>
                        <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase">Error</th>
                        <th className="text-left px-4 py-2.5 text-[9px] text-white/30 uppercase hidden sm:table-cell">Status</th>
                        <th className="text-right px-4 py-2.5 text-[9px] text-white/30 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itOpsLoading && itOpsData ? (
                        [1,2,3].map(i => (
                          <tr key={i} className="border-b border-white/[0.03] animate-pulse">
                            <td colSpan={7} className="px-4 py-3"><div className="h-4 bg-white/5 rounded" /></td>
                          </tr>
                        ))
                      ) : itOpsIncidents.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center" data-testid="it-ops-empty-state">
                            <CheckCircle className="w-8 h-8 text-[#7EBDBC]/30 mx-auto mb-3" />
                            <p className="text-sm text-white/40">No incidents found</p>
                            <p className="text-xs text-white/20 mt-1">There are no technical incidents matching the selected filters.</p>
                          </td>
                        </tr>
                      ) : (
                        itOpsIncidents.map(inc => (
                          <tr key={inc.id} data-testid={`it-ops-incident-row-${inc.id}`} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                            <td className="px-4 py-3 text-xs text-white/60">
                              {inc.created_at ? formatDate(inc.created_at) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase ${
                                inc.severity === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                              }`} data-testid={`it-ops-incident-severity-${inc.id}`}>
                                {inc.severity}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-xs text-white/70 hidden md:table-cell max-w-[150px] truncate">
                              {inc.clinic_name}
                            </td>
                            <td className="px-4 py-3 text-xs text-white/50 hidden lg:table-cell">
                              {inc.operation?.replace(/_/g, ' ')}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-xs text-white/70">{inc.error_code}</div>
                              <div className="text-[10px] text-white/40 max-w-[200px] truncate">{inc.message}</div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full ${
                                inc.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-white/10 text-white/50'
                              }`}>
                                {inc.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {inc.retryable && (
                                <button
                                  onClick={async () => {
                                    setActionLoading(inc.id);
                                    try {
                                      await adminApi("post", `/admin/it-operations/incidents/${inc.id}/retry`);
                                      setToast({ type: "success", message: "Incident queued for retry" });
                                      loadTab();
                                    } catch (err) {
                                      setToast({ type: "error", message: err.response?.data?.detail || "Retry failed" });
                                    } finally {
                                      setActionLoading(null);
                                    }
                                  }}
                                  disabled={actionLoading === inc.id}
                                  className="text-[10px] text-[#7EBDBC] hover:text-white disabled:opacity-50"
                                  data-testid={`retry-incident-${inc.id}`}
                                >
                                  {actionLoading === inc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Retry"}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {itOpsTotal > 20 && (
                  <div className="flex items-center justify-between mt-4 text-xs">
                    <p className="text-white/40">Showing {itOpsIncidents.length} of {itOpsTotal} incidents</p>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setItOpsPage(p => Math.max(1, p - 1))}
                        disabled={itOpsPage <= 1}
                        className="px-2 py-1 rounded bg-white/5 text-white/60 disabled:opacity-30"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1 text-white/60">Page {itOpsPage}</span>
                      <button
                        onClick={() => setItOpsPage(p => p + 1)}
                        disabled={itOpsIncidents.length < 20}
                        className="px-2 py-1 rounded bg-white/5 text-white/60 disabled:opacity-30"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}

                <AdminEmailActivity adminApi={adminApi} clinics={itOpsClinics} />
              </>
            ) : null}
          </div>
        )}
        {tab === "team" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div>
                <h1 className="text-xl font-medium text-white" style={H}>Platform Team</h1>
                <p className="text-xs text-white/40 mt-1">Manage IT Admin accounts and platform access</p>
              </div>
              <button
                onClick={() => { setShowInviteModal(true); setInviteLink(null); setInviteForm({ email: "", full_name: "", platform_role: "it_admin" }); }}
                className="flex items-center gap-2 px-4 py-2 bg-[#205B5A] text-white text-sm rounded-lg hover:bg-[#174544]"
                data-testid="invite-it-admin-btn"
              >
                <Users className="w-4 h-4" /> Invite IT Admin
              </button>
            </div>

            {/* Owner Card */}
            {platformOwner && (
              <div className="bg-[#205B5A]/10 border border-[#205B5A]/30 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#205B5A] rounded-full flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{platformOwner.full_name}</p>
                      <p className="text-xs text-white/50">{platformOwner.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] font-semibold px-2 py-0.5 bg-[#205B5A]/20 text-[#7EBDBC] rounded-full uppercase">
                      Platform Owner
                    </span>
                    <p className="text-[10px] text-white/30 mt-1">Full platform access</p>
                  </div>
                </div>
              </div>
            )}

            {/* IT Admin List */}
            {loading ? (
              <div className="space-y-3">
                {Array(3).fill(0).map((_, i) => (
                  <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 animate-pulse">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-full" />
                      <div className="flex-1">
                        <div className="h-3 w-32 bg-white/10 rounded mb-2" />
                        <div className="h-2 w-48 bg-white/10 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : platformUsers.length === 0 ? (
              <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-12 text-center">
                <Users className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-sm text-white/50 mb-2">No IT Admin accounts yet</p>
                <p className="text-xs text-white/30">Invite IT Admins to help manage platform operations</p>
              </div>
            ) : (
              <div className="space-y-3">
                {platformUsers.map(user => (
                  <div 
                    key={user.id} 
                    className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.05] transition-colors"
                    data-testid={`platform-user-${user.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          user.status === "active" ? "bg-[#7EBDBC]/20" : 
                          user.status === "invited" ? "bg-blue-500/20" : "bg-white/10"
                        }`}>
                          <Users className={`w-5 h-5 ${
                            user.status === "active" ? "text-[#7EBDBC]" : 
                            user.status === "invited" ? "text-blue-400" : "text-white/40"
                          }`} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">{user.full_name || "Pending"}</p>
                          <p className="text-xs text-white/50">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full uppercase ${
                            user.status === "active" ? "bg-[#205B5A]/20 text-[#7EBDBC]" :
                            user.status === "invited" ? "bg-blue-500/20 text-blue-400" :
                            user.status === "deactivated" ? "bg-red-500/20 text-red-400" :
                            "bg-white/10 text-white/40"
                          }`}>
                            {user.status === "it_admin" ? "IT Admin" : user.status}
                          </span>
                          <p className="text-[10px] text-white/30 mt-1">
                            {user.last_login ? `Last login: ${formatDate(user.last_login)}` : 
                             user.invite_expires_at ? `Expires: ${formatDate(user.invite_expires_at)}` : ""}
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedPlatformUser(user)}
                          className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
                          aria-label="View details"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="mt-3 pt-3 border-t border-white/[0.06] flex flex-wrap gap-2">
                      {user.status === "invited" && (
                        <>
                          <button
                            onClick={() => regenerateInvite(user.id)}
                            disabled={actionLoading === "regenerate_" + user.id}
                            className="text-[10px] text-blue-400 bg-blue-400/10 px-2.5 py-1 rounded-full hover:bg-blue-400/20 disabled:opacity-50 flex items-center gap-1"
                          >
                            {actionLoading === "regenerate_" + user.id && <Loader2 className="w-3 h-3 animate-spin" />}
                            Regenerate Link
                          </button>
                          <button
                            onClick={() => revokeInvite(user.id)}
                            disabled={actionLoading === "revoke_" + user.id}
                            className="text-[10px] text-red-400 bg-red-400/10 px-2.5 py-1 rounded-full hover:bg-red-400/20 disabled:opacity-50 flex items-center gap-1"
                          >
                            {actionLoading === "revoke_" + user.id && <Loader2 className="w-3 h-3 animate-spin" />}
                            Revoke
                          </button>
                        </>
                      )}
                      {user.status === "active" && (
                        <>
                          <button
                            onClick={() => revokeSessions(user.id)}
                            disabled={actionLoading === "revoke_sessions_" + user.id}
                            className="text-[10px] text-amber-400 bg-amber-400/10 px-2.5 py-1 rounded-full hover:bg-amber-400/20 disabled:opacity-50 flex items-center gap-1"
                          >
                            {actionLoading === "revoke_sessions_" + user.id && <Loader2 className="w-3 h-3 animate-spin" />}
                            Revoke Sessions
                          </button>
                          <button
                            onClick={() => deactivatePlatformUser(user.id)}
                            disabled={actionLoading === "deactivate_" + user.id}
                            className="text-[10px] text-red-400 bg-red-400/10 px-2.5 py-1 rounded-full hover:bg-red-400/20 disabled:opacity-50 flex items-center gap-1"
                          >
                            {actionLoading === "deactivate_" + user.id && <Loader2 className="w-3 h-3 animate-spin" />}
                            Deactivate
                          </button>
                        </>
                      )}
                      {user.status === "deactivated" && (
                        <button
                          onClick={() => reactivatePlatformUser(user.id)}
                          disabled={actionLoading === "reactivate_" + user.id}
                          className="text-[10px] text-[#7EBDBC] bg-[#7EBDBC]/10 px-2.5 py-1 rounded-full hover:bg-[#7EBDBC]/20 disabled:opacity-50 flex items-center gap-1"
                        >
                          {actionLoading === "reactivate_" + user.id && <Loader2 className="w-3 h-3 animate-spin" />}
                          Reactivate
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/60" onClick={() => { setShowInviteModal(false); setInviteLink(null); }} />
            <div className="relative bg-[#131E1E] rounded-xl p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-lg font-medium text-white mb-4" style={H}>
                {inviteLink ? "Invitation Created" : "Invite IT Admin"}
              </h3>
              
              {inviteLink ? (
                <div>
                  <p className="text-sm text-white/50 mb-4">
                    Copy this link and share it securely with the new IT Admin. This link will only be shown once.
                  </p>
                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4">
                    <p className="text-xs text-white/70 break-all font-mono">{inviteLink}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={copyInviteLink}
                      className="flex-1 px-4 py-2 bg-[#205B5A] text-white rounded-lg text-sm hover:bg-[#174544] flex items-center justify-center gap-2"
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => { setShowInviteModal(false); setInviteLink(null); }}
                      className="px-4 py-2 bg-white/5 border border-white/10 text-white/70 rounded-lg text-sm hover:bg-white/10"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={inviteForm.full_name}
                      onChange={e => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                      placeholder="John Smith"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Email *</label>
                    <input
                      type="email"
                      value={inviteForm.email}
                      onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
                      placeholder="john@company.com"
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1">Role</label>
                    <select
                      value={inviteForm.platform_role}
                      onChange={e => setInviteForm({ ...inviteForm, platform_role: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#7EBDBC]"
                    >
                      <option value="it_admin" className="bg-[#131E1E]">IT Admin</option>
                    </select>
                  </div>
                  <p className="text-[10px] text-white/30">
                    IT Admins can view technical details, retry failed jobs, and resolve incidents. They cannot manage platform accounts.
                  </p>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setShowInviteModal(false)}
                      className="flex-1 px-4 py-2 bg-white/5 border border-white/10 text-white/70 rounded-lg text-sm hover:bg-white/10"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createInvite}
                      disabled={inviteLoading || !inviteForm.email || !inviteForm.full_name}
                      className="flex-1 px-4 py-2 bg-[#205B5A] text-white rounded-lg text-sm hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {inviteLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      Create Invitation
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Clinic detail drawer */}
        {selectedClinic && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-clinic-drawer" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={() => setSelectedClinic(null)} aria-hidden="true" />
            <div className="relative w-full max-w-lg bg-[#131E1E] shadow-2xl overflow-y-auto">
              {clinicDetailLoading ? (
                <div className="p-5 flex items-center justify-center min-h-[200px]">
                  <Loader2 className="w-6 h-6 text-[#7EBDBC] animate-spin" />
                </div>
              ) : (
                <div className="p-5">
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <h2 className="text-lg font-medium text-white" style={H}>{selectedClinic.name}</h2>
                      <p className="text-xs text-white/40">{selectedClinic.owner_name} · {selectedClinic.owner_email}</p>
                    </div>
                    <button onClick={() => setSelectedClinic(null)} className="text-white/30 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
                  </div>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-white/[0.04] rounded-lg p-2.5 text-center">
                      <p className="text-lg font-medium text-[#7EBDBC]" style={H}>{selectedClinic.patient_count}</p>
                      <p className="text-[9px] text-white/30">Patients</p>
                    </div>
                    <div className="bg-white/[0.04] rounded-lg p-2.5 text-center">
                      <p className="text-lg font-medium text-[#7EBDBC]" style={H}>{selectedClinic.appointment_count}</p>
                      <p className="text-[9px] text-white/30">Appts</p>
                    </div>
                    <div className="bg-white/[0.04] rounded-lg p-2.5 text-center">
                      <p className="text-lg font-medium text-[#7EBDBC]" style={H}>{selectedClinic.ai_requests}</p>
                      <p className="text-[9px] text-white/30">AI Requests</p>
                    </div>
                  </div>
                  
                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Status</span>
                      <span className={`font-semibold px-2 py-0.5 rounded-full text-[9px] capitalize ${SS[selectedClinic.subscription_status] || SS.trial}`}>
                        {selectedClinic.subscription_status || "trial"}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Created</span>
                      <span className="text-white/70">{formatDate(selectedClinic.created_at)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Last Activity</span>
                      <span className="text-white/70">{formatDateTime(selectedClinic.last_activity)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Members</span>
                      <span className="text-white/70">{selectedClinic.members?.length}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Practitioners</span>
                      <span className="text-white/70">{selectedClinic.practitioner_count}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">Notes</span>
                      <span className="text-white/70">{selectedClinic.note_count}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">AI Success Rate</span>
                      <span className={`${selectedClinic.ai_success_rate >= 80 ? 'text-[#7EBDBC]' : selectedClinic.ai_success_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {selectedClinic.ai_success_rate}%
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-white/40">AI Tokens</span>
                      <span className="text-white/70">{formatNumber(selectedClinic.ai_total_tokens)}</span>
                    </div>
                  </div>

                  {/* Plan & limits */}
                  <AdminClinicPlan clinicId={selectedClinic.id} adminApi={adminApi} />

                  {/* Feature Flags */}
                  <div className="border-t border-white/[0.06] pt-3 mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[9px] text-white/30 uppercase tracking-wider flex items-center gap-1.5">
                        <Flag className="w-3 h-3" /> Feature Flags
                      </p>
                      {featureFlagsLoading && <Loader2 className="w-3 h-3 text-[#7EBDBC] animate-spin" />}
                    </div>
                    <div className="space-y-2">
                      {FEATURE_FLAGS.map(flag => {
                        const isEnabled = selectedClinic.feature_flags?.[flag.key] !== false;
                        return (
                          <div key={flag.key} className="flex items-center justify-between p-2.5 bg-white/[0.02] rounded-lg hover:bg-white/[0.04]" data-testid={`feature-flag-${flag.key}`}>
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="text-xs text-white/80 font-medium">{flag.label}</p>
                              <p className="text-[10px] text-white/40 truncate">{flag.desc}</p>
                            </div>
                            <button
                              onClick={() => updateFeatureFlag(flag.key, !isEnabled)}
                              disabled={featureFlagsLoading}
                              className={`relative w-10 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7EBDBC]/50 ${isEnabled ? 'bg-[#205B5A]' : 'bg-white/10'} ${featureFlagsLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                              aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${flag.label}`}
                              aria-pressed={isEnabled}
                              data-testid={`feature-flag-toggle-${flag.key}`}
                            >
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isEnabled ? 'left-[22px]' : 'left-0.5'}`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recent Audit History */}
                  {selectedClinic.recent_audit_history?.length > 0 && (
                    <div className="border-t border-white/[0.06] pt-3 mb-4">
                      <p className="text-[9px] text-white/30 uppercase tracking-wider mb-2">Recent Admin Actions</p>
                      <div className="space-y-1">
                        {selectedClinic.recent_audit_history.map((audit, i) => (
                          <div key={i} className="text-[10px] text-white/50">
                            <span className="text-[#7EBDBC] capitalize">{audit.action?.replace(/_/g, " ")}</span>
                            {audit.detail && <span> - {audit.detail}</span>}
                            <span className="text-white/30 ml-1">({formatDate(audit.created_at)})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="border-t border-white/[0.06] pt-3">
                    <p className="text-[9px] text-white/30 uppercase tracking-wider mb-2">Actions</p>
                    <div className="flex flex-wrap gap-1.5">
                      <button 
                        onClick={() => clinicAction(selectedClinic.id, "extend_trial")} 
                        disabled={actionLoading === "extend_trial"}
                        className="text-[10px] text-[#7EBDBC] bg-[#7EBDBC]/10 px-3 py-1.5 rounded-full hover:bg-[#7EBDBC]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {actionLoading === "extend_trial" && <Loader2 className="w-3 h-3 animate-spin" />}
                        Extend trial
                      </button>
                      <button 
                        onClick={() => clinicAction(selectedClinic.id, "activate")} 
                        disabled={actionLoading === "activate"}
                        className="text-[10px] text-[#205B5A] bg-[#205B5A]/20 px-3 py-1.5 rounded-full hover:bg-[#205B5A]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {actionLoading === "activate" && <Loader2 className="w-3 h-3 animate-spin" />}
                        Activate paid
                      </button>
                      <button 
                        onClick={() => clinicAction(selectedClinic.id, "suspend")} 
                        disabled={actionLoading === "suspend"}
                        className="text-[10px] text-amber-400 bg-amber-400/10 px-3 py-1.5 rounded-full hover:bg-amber-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {actionLoading === "suspend" && <Loader2 className="w-3 h-3 animate-spin" />}
                        Suspend
                      </button>
                      <button 
                        onClick={() => clinicAction(selectedClinic.id, "reactivate")} 
                        disabled={actionLoading === "reactivate"}
                        className="text-[10px] text-[#7EBDBC] bg-[#7EBDBC]/10 px-3 py-1.5 rounded-full hover:bg-[#7EBDBC]/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {actionLoading === "reactivate" && <Loader2 className="w-3 h-3 animate-spin" />}
                        Reactivate
                      </button>
                      <button 
                        onClick={() => clinicAction(selectedClinic.id, "cancel")} 
                        disabled={actionLoading === "cancel"}
                        className="text-[10px] text-red-400 bg-red-400/10 px-3 py-1.5 rounded-full hover:bg-red-400/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {actionLoading === "cancel" && <Loader2 className="w-3 h-3 animate-spin" />}
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Confirmation Modal */}
        {confirmAction && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/60" onClick={() => { setConfirmAction(null); setActionReason(""); }} />
            <div className="relative bg-[#131E1E] rounded-xl p-6 max-w-sm w-full shadow-2xl">
              <h3 className="text-lg font-medium text-white mb-2" style={H}>
                Confirm {confirmAction.action.replace(/_/g, " ")}
              </h3>
              <p className="text-sm text-white/50 mb-4">
                This action will be logged. Please provide a reason.
              </p>
              <textarea
                value={actionReason}
                onChange={e => setActionReason(e.target.value)}
                placeholder="Reason for this action..."
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] min-h-[80px] mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setConfirmAction(null); setActionReason(""); }}
                  className="flex-1 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/70 text-sm hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={() => clinicAction(confirmAction.clinicId, confirmAction.action, actionReason)}
                  disabled={!actionReason.trim() || actionLoading}
                  className="flex-1 px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
